import axios, { InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';

// API Base URL 설정
// 런타임 환경변수 우선 사용 (Docker 배포 시 주입됨)
// 폴백: import.meta.env (빌드 타임) → '/api' (기본값)
const API_BASE_URL =
  (window as any).__RUNTIME_CONFIG__?.VITE_API_URL ||
  import.meta.env.VITE_API_URL ||
  '/api';

// 개발 모드 확인
const isDev = import.meta.env.DEV;

// 요청 메타데이터 타입 확장
interface RequestMetadata {
  startTime: number;
  duration?: number;
}

declare module 'axios' {
  interface InternalAxiosRequestConfig {
    metadata?: RequestMetadata;
  }
}

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json; charset=utf-8',
  },
  withCredentials: true,  // 쿠키 전송 활성화
});

// 자동 재시도 설정 (네트워크 일시 장애 대비)
// axios-retry 대신 interceptor로 직접 구현
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1초

api.interceptors.response.use(
  undefined,
  async (error: AxiosError) => {
    const config = error.config as any;

    // 백엔드 예외 핸들러는 {error: {code, message}} 로 답하는데 화면 코드는 detail 을 읽는다.
    // 여기서 한 번 맞춰 주면 서버가 보낸 안내가 그대로 보인다 (#65)
    const body = error.response?.data as any;
    if (body?.error?.message && !body.detail) {
      body.detail = body.error.message;
    }

    // 재시도 카운트 초기화
    config.__retryCount = config.__retryCount ?? 0;

    // 재시도 조건: 네트워크 오류 또는 5xx 서버 에러.
    // __noRetry 요청은 다시 보내지 않는다 — 구글 인증 코드처럼 한 번만 쓸 수 있는 값을 보내는 요청 (#51)
    const shouldRetry =
      !config.__noRetry &&
      (!error.response || // 네트워크 오류
        (error.response.status >= 500 && error.response.status < 600)); // 5xx 에러

    // 최대 재시도 횟수를 초과하지 않고, 재시도 조건을 만족하는 경우
    if (config.__retryCount < MAX_RETRIES && shouldRetry) {
      config.__retryCount += 1;

      if (isDev) {
        console.warn(
          `🔄 재시도 ${config.__retryCount}/${MAX_RETRIES}: ${config.method?.toUpperCase()} ${config.url}`,
          error.message
        );
      }

      // 지수 백오프 (1초, 2초, 4초)
      const delay = RETRY_DELAY * Math.pow(2, config.__retryCount - 1);
      await new Promise(resolve => setTimeout(resolve, delay));

      return api.request(config);
    }

    return Promise.reject(error);
  }
);

// 요청 시간 측정을 위한 메타데이터 추가
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  config.metadata = { startTime: Date.now() };
  return config;
});

// 요청 인터셉터 - 토큰 자동 추가 및 로깅
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // 개발 모드에서 요청 로깅 (Vite 서버 터미널에만 출력)
    if (isDev) {
      const method = config.method?.toUpperCase() || 'GET';
      const url = `${config.baseURL}${config.url}`;
      const timestamp = new Date().toISOString();
      
      // Vite 서버 터미널에 출력
      try {
        fetch('/__client-log', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            level: 'request',
            message: `${method} ${url}`,
            data: {
              method,
              url,
              timestamp,
              params: config.params,
              body: config.data,
            },
          }),
        }).catch(() => {
          // 실패해도 무시 (서버가 없거나 연결 실패 시)
        });
      } catch {
        // fetch 실패 시 무시
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 응답 인터셉터 - 토큰 만료 처리 및 로깅
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // 응답 시간 계산
    if (response.config.metadata) {
      response.config.metadata.duration = Date.now() - response.config.metadata.startTime;
    }
    
    // 개발 모드에서 응답 로깅 (Vite 서버 터미널에만 출력)
    if (isDev) {
      const method = response.config.method?.toUpperCase() || 'GET';
      const url = `${response.config.baseURL}${response.config.url}`;
      const status = response.status;
      const statusText = response.statusText;
      const timestamp = new Date().toISOString();
      const duration = response.config.metadata?.duration || 'N/A';
      
      // Vite 서버 터미널에 출력
      try {
        fetch('/__client-log', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            level: 'response',
            message: `${method} ${url} ${status} ${statusText}`,
            data: {
              method,
              url,
              status,
              statusText,
              duration: duration !== 'N/A' ? duration : undefined,
              timestamp,
            },
          }),
        }).catch(() => {
          // 실패해도 무시
        });
      } catch {
        // fetch 실패 시 무시
      }
    }
    
    return response;
  },
  (error) => {
    // 응답 시간 계산
    if (error.config?.metadata) {
      error.config.metadata.duration = Date.now() - error.config.metadata.startTime;
    }
    
    // 개발 모드에서 에러 로깅 (Vite 서버 터미널에만 출력)
    if (isDev) {
      const method = error.config?.method?.toUpperCase() || 'GET';
      const url = error.config ? `${error.config.baseURL}${error.config.url}` : 'Unknown';
      const status = error.response?.status || 'N/A';
      const statusText = error.response?.statusText || error.message;
      const timestamp = new Date().toISOString();
      const duration = error.config?.metadata?.duration || 'N/A';

      // 응답 데이터 필터링: HTML 응답이거나 5xx 에러는 로깅하지 않음
      let responseData = undefined;
      if (error.response?.data) {
        const contentType = error.response.headers?.['content-type'] || '';
        const isHtml = contentType.includes('text/html');
        const is5xxError = typeof status === 'number' && status >= 500;

        // HTML이 아니고 5xx 에러가 아닌 경우에만 로깅
        if (!isHtml && !is5xxError) {
          const data = error.response.data;
          // 문자열인 경우 200자로 제한
          if (typeof data === 'string') {
            responseData = data.length > 200 ? data.substring(0, 200) + '...' : data;
          } else {
            responseData = data;
          }
        }
      }

      // Vite 서버 터미널에 출력
      try {
        fetch('/__client-log', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            level: 'error',
            message: `${method} ${url} ${status} ${statusText}`,
            data: {
              method,
              url,
              status: status !== 'N/A' ? status : undefined,
              statusText,
              duration: duration !== 'N/A' ? duration : undefined,
              error: error.message,
              responseData,
              timestamp,
            },
          }),
        }).catch(() => {
          // 실패해도 무시
        });
      } catch {
        // fetch 실패 시 무시
      }
    }
    
    // 로그인 페이지에서는 자동 리디렉션하지 않음 (에러 메시지 표시를 위해)
    const basename =
      (window as any).__RUNTIME_CONFIG__?.VITE_BASE_PATH?.replace(/\/$/, '') ||
      import.meta.env.VITE_BASE_PATH ||
      '';
    const loginPath = basename ? `${basename}/login` : '/login';
    const rootPath = basename ? `${basename}/` : '/';
    const isLoginPage = window.location.pathname === loginPath || window.location.pathname === rootPath;
    if (error.response?.status === 401 && !isLoginPage) {
      localStorage.removeItem('token');
      // React Router를 사용하는 컴포넌트에서 처리할 수 있도록 커스텀 이벤트 발생
      // App.tsx나 상위 컴포넌트에서 이 이벤트를 리스닝하여 navigate로 처리
      const redirectEvent = new CustomEvent('auth:redirect', { 
        detail: { path: loginPath } 
      });
      window.dispatchEvent(redirectEvent);
      // 이벤트가 처리되지 않으면 fallback으로 window.location.href 사용
      setTimeout(() => {
        if (window.location.pathname !== loginPath && window.location.pathname !== rootPath) {
          window.location.href = loginPath;
        }
      }, 100);
    }
    return Promise.reject(error);
  }
);
